import React, { FC, forwardRef, useRef, useState } from 'react'
import { useDeviceDetection, useOnClickOutside } from '@dostavkee/react-hooks'
import { DateRange } from 'react-day-picker'

import './calendar.css'

import { IconCalendar } from '../icon'
import { Input, InputProps } from '../input'
import { CalendarDropdown } from './calendar-dropdown'
import { CalendarModal } from './calendar-modal'
import styles from './calendar-range.module.scss'
import { formatDateRange } from './utils/format-date-range'

export interface CalendarRangeProps extends Omit<InputProps, 'onChange' | 'value'> {
    id?: string
    onChange: (value?: DateRange) => void
    value?: DateRange
    getInjectedEl?: () => HTMLElement
    hasMaxDate?: boolean
    selectMode?: 'modal' | 'dropdown' | 'default'
}

export const CalendarRange: FC<CalendarRangeProps> = forwardRef<
    HTMLInputElement,
    CalendarRangeProps
>(
    (
        {
            placeholder,
            onChange,
            value,
            isInvalid,
            errorMessage,
            label,
            id,
            disabled,
            getInjectedEl,
            hasMaxDate = true,
            selectMode,
            ...props
        },
        ref
    ) => {
        const [isOpen, setIsOpen] = useState(false)

        const dropdownRef = useRef<HTMLDivElement>(null)

        const { isMobile } = useDeviceDetection()

        const handleClose = React.useCallback(() => {
            setIsOpen(false)
        }, [])

        useOnClickOutside(dropdownRef, handleClose)

        const renderModal = () => {
            return (
                <CalendarModal
                    isOpen
                    getInjectedEl={getInjectedEl}
                    hasMaxDate={hasMaxDate}
                    value={value}
                    onChange={onChange}
                    onClose={handleClose}
                />
            )
        }

        const renderDropdown = () => {
            return (
                <CalendarDropdown
                    ref={dropdownRef}
                    hasMaxDate={hasMaxDate}
                    value={value}
                    onChange={onChange}
                    onClose={handleClose}
                />
            )
        }

        const renderCalendar = () => {
            if (selectMode === 'modal') return renderModal()
            if (selectMode === 'dropdown') return renderDropdown()

            return isMobile ? renderModal() : renderDropdown()
        }

        return (
            <div className={styles['calendar']}>
                <Input
                    {...props}
                    ref={ref}
                    disabled={disabled}
                    errorMessage={errorMessage}
                    id={id}
                    isInvalid={isInvalid}
                    label={label}
                    placeholder={placeholder}
                    postfixEl={<IconCalendar color='var(--text-and-icon-secondary)' size={22} />}
                    type='button'
                    value={formatDateRange(value)}
                    onClick={() => {
                        setIsOpen(true)
                    }}
                />
                {isOpen && renderCalendar()}
            </div>
        )
    }
)

CalendarRange.displayName = 'Calendar'
